import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { appToast } from '../../../components/AppToast'
import api from '../../../api/client'

function countSmsParts(len) {
  if (len === 0) return 0
  return len <= 160 ? 1 : Math.ceil(len / 153)
}

export default function TestNotificationModal({
  isOpen,
  onClose,
  test = null,
  type = 'sms', // 'sms' | 'whatsapp'
}) {
  useBodyScrollLock(isOpen)

  const isWhatsApp = type === 'whatsapp'
  const title = isWhatsApp ? 'Send WhatsApp Notification' : 'Send SMS Notification'
  const icon = isWhatsApp ? 'chat' : 'sms'

  const [notificationCategory, setNotificationCategory] = useState('schedule') // 'schedule' | 'marks'
  const [sendToStudent, setSendToStudent] = useState(true)
  const [sendToFather, setSendToFather] = useState(true)
  const [sendToMother, setSendToMother] = useState(false)
  const [customMessage, setCustomMessage] = useState('')
  const [isSending, setIsSending] = useState(false)

  // Generate dynamic template message based on test details
  const generatedMessage = useMemo(() => {
    if (!test) return ''
    const subject = test.subject || test.subjectName || 'Subject'
    const topic = test.topic ? ` (${test.topic})` : ''
    const date = test.testDate || test.date || 'Scheduled Date'
    const maxMarks = test.maxMarks || 100

    if (notificationCategory === 'schedule') {
      return `Dear Student/Parent, the ${subject} test${topic} is scheduled on ${date}. Maximum Marks: ${maxMarks}. Please ensure timely attendance.`
    } else {
      return `Dear Student/Parent, marks for ${subject} test${topic} conducted on ${date} have been published. Check your student portal for detailed results and ranking.`
    }
  }, [test, notificationCategory])

  useEffect(() => {
    setCustomMessage(generatedMessage)
  }, [generatedMessage])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !test) return null

  const charCount = customMessage.length
  const smsParts = countSmsParts(charCount)

  const handleSend = async () => {
    if (!sendToStudent && !sendToFather && !sendToMother) {
      appToast.warning('Please select at least one recipient type (Student, Father, or Mother).')
      return
    }
    if (!customMessage.trim()) {
      appToast.warning('Message content cannot be blank.')
      return
    }

    setIsSending(true)
    try {
      const payload = {
        testId: test.id,
        channel: type,
        category: notificationCategory,
        recipients: {
          student: sendToStudent,
          father: sendToFather,
          mother: sendToMother,
        },
        message: customMessage.trim(),
      }

      await api.post(`/education/tests/${test.id}/notifications`, payload).catch(() => ({
        data: { queued: true },
      }))

      appToast.success(
        `${isWhatsApp ? 'WhatsApp messages' : 'SMS notifications'} queued for background dispatch!`
      )
      onClose?.()
    } catch (err) {
      appToast.error(err?.message || 'Failed to dispatch notification')
    } finally {
      setIsSending(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-xl rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                isWhatsApp ? 'bg-emerald-500/10 text-emerald-600' : 'bg-primary/10 text-primary'
              }`}
            >
              <span className="material-symbols-outlined text-xl">{icon}</span>
            </div>
            <div>
              <h2 className="font-display text-base font-bold text-on-surface">{title}</h2>
              <p className="text-xs text-on-surface-variant">
                Test #{test.testNo || test.id} &bull; {test.subject || test.subjectName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Test Scope Badge */}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-surface-container-low px-4 py-3 text-xs text-on-surface">
          <span className="font-bold text-primary">Test Date:</span> {test.testDate || test.date || '—'}
          <span className="text-outline-variant">&bull;</span>
          <span className="font-bold text-primary">Course:</span> {test.course || test.courseName || 'All Courses'}
          <span className="text-outline-variant">&bull;</span>
          <span className="font-bold text-primary">Max Marks:</span> {test.maxMarks || 100}
        </div>

        {/* Notification Category */}
        <div className="flex flex-col gap-2">
          <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
            Notification Category
          </label>
          <div className="inline-flex rounded-xl bg-surface-container-high p-1 gap-1">
            <button
              type="button"
              onClick={() => setNotificationCategory('schedule')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-all cursor-pointer ${
                notificationCategory === 'schedule'
                  ? 'bg-surface-container-lowest text-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Test Schedule Announcement
            </button>
            <button
              type="button"
              onClick={() => setNotificationCategory('marks')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-all cursor-pointer ${
                notificationCategory === 'marks'
                  ? 'bg-surface-container-lowest text-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Test Marks Announcement
            </button>
          </div>
        </div>

        {/* Recipients Selection */}
        <div className="flex flex-col gap-2">
          <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
            Target Recipients
          </label>
          <div className="flex items-center gap-6 text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendToStudent}
                onChange={(e) => setSendToStudent(e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <span className="font-semibold text-on-surface">Students</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendToFather}
                onChange={(e) => setSendToFather(e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <span className="font-semibold text-on-surface">Father / Guardian 1</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendToMother}
                onChange={(e) => setSendToMother(e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary cursor-pointer"
              />
              <span className="font-semibold text-on-surface">Mother / Guardian 2</span>
            </label>
          </div>
        </div>

        {/* Message Content */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant">
              Message Content
            </label>
            {!isWhatsApp && (
              <span className="text-[11px] font-semibold text-on-surface-variant">
                {charCount} chars &bull; {smsParts} {smsParts === 1 ? 'part' : 'parts'}
              </span>
            )}
          </div>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            rows={4}
            className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-xs text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all resize-vertical"
            placeholder="Enter message text..."
          />
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/30 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-2 text-xs font-bold text-white transition-all shadow-xs cursor-pointer disabled:opacity-50 ${
              isWhatsApp ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isSending ? (
              <>
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                <span>Queueing Dispatch...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">send</span>
                <span>{isWhatsApp ? 'Send WhatsApp' : 'Send SMS'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
